import PropTypes from 'prop-types';
import React, {createRef} from 'react';
import {FormattedMessage} from 'react-intl';
import classNames from 'classnames';
import memoizeOne from 'memoize-one';

import {Posts, Preferences} from 'mattermost-redux/constants';
import * as ReduxPostUtils from 'mattermost-redux/utils/post_utils';

import Constants, {Locations, A11yCustomEventTypes, AppEvents} from 'utils/constants';
import * as PostUtils from 'utils/post_utils';
import * as Utils from 'utils/utils';
import ActionsMenu from 'components/actions_menu';
import DotMenu from 'components/post_menus/dot_menu';
import FileAttachmentListContainer from 'components/file_attachment_list';
import OverlayTrigger from 'components/overlay_trigger';
import Tooltip from 'components/tooltip';
import PostProfilePicture from 'components/post_profile_picture';
import PostAriaLabelDiv from 'components/post_view/post_aria_label_div';
import PostFlagIcon from 'components/post_view/post_flag_icon';
import ReactionList from 'components/post_view/reaction_list';
import PostTime from 'components/post_view/post_time';
import PostRecentReactions from 'components/post_view/post_recent_reactions';
import PostReaction from 'components/post_view/post_reaction';
import MessageWithAdditionalContent from 'components/message_with_additional_content';

import {BotBadge} from '@time-webkit/all/atoms';

import InfoSmallIcon from 'components/widgets/icons/info_small_icon';
import {PostContextMenu} from 'components/post_menus/post_context_menu/post_context_menu';

import UserProfile from 'components/user_profile';
import PostPreHeader from 'components/post_view/post_pre_header';
import CustomStatusEmoji from 'components/custom_status/custom_status_emoji';
import EditPost from 'components/edit_post';
import AutoHeightSwitcher, {AutoHeightSlots} from 'components/common/auto_height_switcher';
import HideWhenChannelIsReadOnly from 'features/channels/components/read-only';
import {logPostAction} from 'utils/log_post_action';
import PostContext from '../post_view/post_context';

const EMOJI_STYLE = {
    marginLeft: 4,
    marginTop: 2,
};

export default class RhsRootPost extends React.PureComponent {
    static get propTypes() {
        return {
            post: PropTypes.object.isRequired,
            teamId: PropTypes.string.isRequired,
            currentUserId: PropTypes.string.isRequired,
            compactDisplay: PropTypes.bool,
            commentCount: PropTypes.number.isRequired,
            isFlagged: PropTypes.bool.isRequired,
            previewCollapsed: PropTypes.string,
            previewEnabled: PropTypes.bool,
            isEmbedVisible: PropTypes.bool,
            enableEmojiPicker: PropTypes.bool.isRequired,
            overrideUsername: PropTypes.string,
            pluginPostTypes: PropTypes.object,
            channelIsArchived: PropTypes.bool.isRequired,
            handleCardClick: PropTypes.func.isRequired,
            newEditorEnabled: PropTypes.bool,

            /**
         * To Check if the current post is last in the list of RHS
         */
            isLastPost: PropTypes.bool,

            /**
         * To check if the state of emoji for last message and from where it was emitted
         */
            shortcutReactToLastPostEmittedFrom: PropTypes.string,
            actions: PropTypes.shape({
                markPostAsUnread: PropTypes.func.isRequired,

                /**
             * Function to set or unset emoji picker for last message
             */
                emitShortcutReactToLastPostFrom: PropTypes.func,

                /**
             * Function to set viewed Actions Menu for first time
             */
                setActionsMenuInitialisationState: PropTypes.func,
            }),
            timestampProps: PropTypes.object,
            isBot: PropTypes.bool,
            shouldShowActionsMenu: PropTypes.bool,

            /**
         * true when want to show the Actions Menu with pulsating dot for tutorial
         */
            showActionsMenuPulsatingDot: PropTypes.bool,
            oneClickReactionsEnabled: PropTypes.bool,
            recentEmojis: PropTypes.arrayOf(PropTypes.object),

            isExpanded: PropTypes.bool,

            /**
         * check if the current post is being edited at the moment
         */
            isPostBeingEdited: PropTypes.bool,
            isMobileView: PropTypes.bool.isRequired,
        };
    }

    static defaultProps = {
        commentCount: 0,
    };

    constructor(props) {
        super(props);

        this.state = {
            alt: false,
            showActionsMenu: false,
            showActionTip: false,
            showDotMenu: false,
            showPostContextMenu: false,
            showEmojiPicker: false,
            testStateObj: true,
            fileDropdownOpened: false,
            hover: false,
            a11yActive: false,
        };

        this.postHeaderRef = createRef();
        this.dotMenuRef = createRef();
        this.postRef = createRef();
    }

    getPostContextValue = memoizeOne(() => {
        return {location: Locations.RHS_ROOT, onLogPostAction: this.trackPostAction};
    })

    handleShortcutReactToLastPost = (isLastPost) => {
        if (isLastPost) {
            const {
                channelIsArchived,
                enableEmojiPicker,
                isMobileView,
                post,
                actions: {emitShortcutReactToLastPostFrom},
            } = this.props;

            // Setting the last message emoji action to empty to clean up the redux state
            emitShortcutReactToLastPostFrom(Locations.NO_WHERE);

            // Following are the types of posts on which adding reaction is not possible
            const isDeletedPost = post && post.state === Posts.POST_DELETED;
            const isEphemeralPost = post && Utils.isPostEphemeral(post);
            const isSystemMessage = post && PostUtils.isSystemMessage(post);
            const isFailedPost = post && post.failed;
            const isPostsFakeParentDeleted = post && post.type === Constants.PostTypes.FAKE_PARENT_DELETED;

            // Checking if rhs root comment is at scroll view of the user
            const boundingRectOfPostInfo = this.postHeaderRef.current.getBoundingClientRect();
            const isPostHeaderVisibleToUser =
                boundingRectOfPostInfo.top - 110 > 0 && boundingRectOfPostInfo.bottom < window.innerHeight;

            if (isPostHeaderVisibleToUser) {
                if (
                    !isEphemeralPost &&
                    !isSystemMessage &&
                    !isDeletedPost &&
                    !isFailedPost &&
                    !isMobileView &&
                    !channelIsArchived &&
                    !isPostsFakeParentDeleted &&
                    enableEmojiPicker
                ) {
                    // As per issue in #2 of mattermost-webapp/pull/4478#pullrequestreview-339313236
                    // We are not not handling focus condition as we did for rhs_comment as the dot menu is already in dom and not visible
                    this.toggleEmojiPicker(isLastPost);
                    this.trackPostAction('addReaction', 'hotkey');
                }
            }
        }
    };

    handleCopy = () => {
        this.trackPostAction('copy', 'rightClick');
    };

    componentDidMount() {
        if (this.postRef.current) {
            this.postRef.current.addEventListener(A11yCustomEventTypes.ACTIVATE, this.handleA11yActivateEvent);
            this.postRef.current.addEventListener(A11yCustomEventTypes.DEACTIVATE, this.handleA11yDeactivateEvent);
        }
    }
    componentWillUnmount() {
        this.removeKeyboardListeners();

        if (this.postRef.current) {
            this.postRef.current.removeEventListener(A11yCustomEventTypes.ACTIVATE, this.handleA11yActivateEvent);
            this.postRef.current.removeEventListener(A11yCustomEventTypes.DEACTIVATE, this.handleA11yDeactivateEvent);
        }
    }

    componentDidUpdate(prevProps) {
        const {shortcutReactToLastPostEmittedFrom, isLastPost} = this.props;

        if (this.state.a11yActive) {
            this.postRef.current.dispatchEvent(new Event(A11yCustomEventTypes.UPDATE));
        }

        const shortcutReactToLastPostEmittedFromRHS =
            prevProps.shortcutReactToLastPostEmittedFrom !== shortcutReactToLastPostEmittedFrom &&
            shortcutReactToLastPostEmittedFrom === Locations.RHS_ROOT;
        if (shortcutReactToLastPostEmittedFromRHS) {
            this.handleShortcutReactToLastPost(isLastPost);
        }
    }

    renderPostTime = (isEphemeral) => {
        const post = this.props.post;

        if (post.type === Constants.PostTypes.FAKE_PARENT_DELETED) {
            return null;
        }

        const isPermalink = !(
            isEphemeral ||
            Posts.POST_DELETED === post.state ||
            ReduxPostUtils.isPostPendingOrFailed(post)
        );

        return (
            <PostTime
                isPermalink={isPermalink}
                eventTime={post.create_at}
                postId={post.id}
                timestampProps={this.props.timestampProps}
            />
        );
    };

    toggleEmojiPicker = () => this.setState((prevState) => ({showEmojiPicker: !prevState.showEmojiPicker}));

    toggleEmojiPickerInPopup = () => {
        if (!this.state.showEmojiPicker) {
            this.trackPostAction('addReaction', 'popup');
        }
        this.toggleEmojiPicker();
    };

    handleA11yActivateEvent = () => {
        this.setState({a11yActive: true});
    };

    handleA11yDeactivateEvent = () => {
        this.setState({a11yActive: false});
    };

    getClassName = (post, isSystemMessage, isMeMessage) => {
        let className = 'post post--root post--thread';
        if (this.props.currentUserId === post.user_id) {
            className += ' current--user';
        }

        if (this.props.isPostBeingEdited) {
            className += ' post--editing';
        }

        if (isSystemMessage || isMeMessage) {
            className += ' post--system';
        }

        if (this.props.compactDisplay) {
            className += ' post--compact';
        }

        if (
            this.state.showDotMenu ||
            this.state.showActionsMenu ||
            this.state.showActionTip ||
            this.state.fileDropdownOpened ||
            this.state.showEmojiPicker ||
            this.state.showPostContextMenu
        ) {
            className += ' post--hovered';
        }

        if (this.state.alt && !this.props.channelIsArchived) {
            className += ' cursor--pointer';
        }

        return className;
    };

    setHover = (e) => {
        this.setState({
            hover: true,
            alt: e.altKey,
        });

        this.addKeyboardListeners();
    };

    unsetHover = () => {
        this.setState({
            hover: false,
            alt: false,
        });

        this.removeKeyboardListeners();
    };

    addKeyboardListeners = () => {
        document.addEventListener('keydown', this.handleAlt);
        document.addEventListener('keyup', this.handleAlt);
    };

    removeKeyboardListeners = () => {
        document.removeEventListener('keydown', this.handleAlt);
        document.removeEventListener('keyup', this.handleAlt);
    };

    handleAlt = (e) => {
        if (this.state.alt !== e.altKey) {
            this.setState({alt: e.altKey});
        }
    };

    handleActionsMenuOpened = (open) => {
        if (this.props.showActionsMenuPulsatingDot) {
            this.setState({showActionTip: true});
            return;
        }
        this.setState({showActionsMenu: open});
    };

    handleDotMenuOpened = (open) => {
        this.setState({showDotMenu: open});
    };

    handlePostContextMenuOpened = (open) => {
        this.setState({showPostContextMenu: open});
    };

    handleActionsMenuTipOpened = () => {
        this.setState({showActionTip: true});
    };

    handleActionsMenuGotItClick = () => {
        this.props.actions.setActionsMenuInitialisationState?.({[Preferences.ACTIONS_MENU_VIEWED]: true});
        this.setState({showActionTip: false});
    };

    handleTipDismissed = () => {
        this.setState({showActionTip: false});
    };

    handleFileDropdownOpened = (open) => {
        this.setState({fileDropdownOpened: open});
    };

    handlePostClick = (e) => {
        if (this.props.channelIsArchived) {
            return;
        }

        if (e.altKey) {
            this.props.actions.markPostAsUnread(this.props.post, 'RHS_ROOT');
        }
    };

    handleProfilePopoverOpen = () => {
        this.trackPostAction('profile', 'click');
    }

    getDotMenuRef = () => {
        return this.dotMenuRef.current;
    };

    trackPostAction = (action, inputType) => {
        logPostAction(action, inputType, Locations.RHS_ROOT, this.props.post);
    }

    render() {
        const {channelIsArchived, isBot, isMobileView, post, teamId, isPostBeingEdited, overrideUsername} = this.props;

        const isPostDeleted = post && post.state === Posts.POST_DELETED;
        const isEphemeral = Utils.isPostEphemeral(post);
        const isSystemMessage = PostUtils.isSystemMessage(post);
        const isMeMessage = ReduxPostUtils.isMeMessage(post);

        const showRecentlyUsedReactions =
            !isEphemeral &&
            !post.failed &&
            !isSystemMessage &&
            !channelIsArchived &&
            this.props.oneClickReactionsEnabled &&
            this.props.enableEmojiPicker;
        let showRecentReacions;
        if (showRecentlyUsedReactions) {
            showRecentReacions = (
                <PostRecentReactions
                    channelId={post.channel_id}
                    postId={post.id}
                    teamId={this.props.teamId}
                    emojis={this.props.recentEmojis}
                    getDotMenuRef={this.getDotMenuRef}
                    size={this.props.isExpanded ? 3 : 1}
                />
            );
        }

        let postReaction;
        if (
            !isEphemeral &&
            !post.failed &&
            !isSystemMessage &&
            this.props.enableEmojiPicker &&
            !channelIsArchived
        ) {
            postReaction = (
                <PostReaction
                    channelId={post.channel_id}
                    postId={post.id}
                    teamId={teamId}
                    getDotMenuRef={this.getDotMenuRef}
                    showEmojiPicker={this.state.showEmojiPicker}
                    toggleEmojiPicker={this.toggleEmojiPickerInPopup}
                />
            );
        }

        let fileAttachment = null;
        if (post.file_ids && post.file_ids.length > 0) {
            fileAttachment = (
                <FileAttachmentListContainer
                    post={post}
                    compactDisplay={this.props.compactDisplay}
                    handleFileDropdownOpened={this.handleFileDropdownOpened}
                />
            );
        }

        let userProfile;
        let botIndicator;
        if (isSystemMessage) {
            userProfile = (
                <UserProfile
                    overwriteName={
                        <FormattedMessage
                            id='post_info.system'
                            defaultMessage='System'
                        />
                    }
                    overwriteImage={Constants.SYSTEM_MESSAGE_PROFILE_IMAGE}
                    disablePopover={true}
                />
            );
        } else if (post.props?.from_webhook) {
            userProfile = (
                <UserProfile
                    key={post.user_id}
                    userId={post.user_id}
                    hideStatus={true}
                    overwriteName={overrideUsername}
                    disablePopover={true}
                />
            );

            botIndicator = <BotBadge />;
        } else {
            userProfile = (
                <UserProfile
                    key={post.user_id}
                    userId={post.user_id}
                    isRHS={true}
                    hasMention={true}
                    onProfilePopoverOpen={this.handleProfilePopoverOpen}
                />
            );
        }

        const postClass = classNames('post__body--transition', {'post--edited': PostUtils.isEdited(this.props.post)});

        const actionsMenu = (
            <HideWhenChannelIsReadOnly channelId={this.props.post.channel_id}>
                <ActionsMenu
                    post={this.props.post}
                    location={Locations.RHS_ROOT}
                    handleDropdownOpened={this.handleActionsMenuOpened}
                    isMenuOpen={this.state.showActionsMenu}
                    showPulsatingDot={this.props.showActionsMenuPulsatingDot}
                    showTutorialTip={this.state.showActionTip}
                    handleOpenTip={this.handleActionsMenuTipOpened}
                    handleNextTip={this.handleActionsMenuGotItClick}
                    handleDismissTip={this.handleTipDismissed}
                />
            </HideWhenChannelIsReadOnly>
        );

        const dotMenu = (
            <DotMenu
                post={this.props.post}
                onDropdownOpened={this.handleDotMenuOpened}
                onToggleEmojiPicker={this.toggleEmojiPicker}
                isMenuOpen={this.state.showDotMenu}
            />
        );

        let postFlagIcon;
        const showFlagIcon = !isEphemeral && !post.failed && !isSystemMessage && !isMobileView;
        if (showFlagIcon) {
            postFlagIcon = (
                <HideWhenChannelIsReadOnly channelId={this.props.post.channel_id}>
                    <PostFlagIcon
                        postId={post.id}
                        isFlagged={this.props.isFlagged}
                    />
                </HideWhenChannelIsReadOnly>
            );
        }

        let dotMenuContainer;
        if (!isPostDeleted && this.props.post.type !== Constants.PostTypes.FAKE_PARENT_DELETED) {
            dotMenuContainer = (
                <div
                    ref={this.dotMenuRef}
                    className='col post-menu'
                >
                    {showRecentReacions}
                    {postReaction}
                    {postFlagIcon}
                    {actionsMenu}
                    {dotMenu}
                </div>
            );
        }

        let postInfoIcon;
        if (this.props.post.props && this.props.post.props.card) {
            postInfoIcon = (
                <OverlayTrigger
                    delayShow={Constants.OVERLAY_TIME_DELAY}
                    placement='top'
                    overlay={
                        <Tooltip>
                            <FormattedMessage
                                id='post_info.info.view_additional_info'
                                defaultMessage='View additional info'
                            />
                        </Tooltip>
                    }
                >
                    <button
                        className='card-icon__container icon--show style--none'
                        onClick={(e) => {
                            e.preventDefault();
                            this.props.handleCardClick(this.props.post);
                        }}
                    >
                        <InfoSmallIcon
                            className='icon icon__info'
                            aria-hidden='true'
                        />
                    </button>
                </OverlayTrigger>
            );
        }

        let customStatus;
        if (!(isSystemMessage || post?.props?.from_webhook || isBot)) {
            customStatus = (
                <CustomStatusEmoji
                    userID={post.user_id}
                    showTooltip={true}
                    emojiStyle={EMOJI_STYLE}
                />
            );
        }

        const message = (
            <MessageWithAdditionalContent
                post={post}
                previewCollapsed={this.props.previewCollapsed}
                previewEnabled={this.props.previewEnabled}
                isEmbedVisible={this.props.isEmbedVisible}
                pluginPostTypes={this.props.pluginPostTypes}
            />
        );

        const showSlot = isPostBeingEdited && !this.props.newEditorEnabled ? AutoHeightSlots.SLOT2 : AutoHeightSlots.SLOT1;

        return (
            <PostContext.Provider value={this.getPostContextValue()}>
                <PostContextMenu
                    post={post}
                    open={this.state.showPostContextMenu}
                    disabled={!this.state.hover && !this.state.showPostContextMenu}
                    onToggle={this.handlePostContextMenuOpened}
                >
                    <PostAriaLabelDiv
                        data-testid='root-post'
                        ref={this.postRef}
                        role='listitem'
                        id={'rhsPost_' + post.id}
                        tabIndex='-1'
                        post={post}
                        className={`thread__root a11y__section ${this.getClassName(post, isSystemMessage, isMeMessage)}`}
                        onClick={this.handlePostClick}
                        onMouseOver={this.setHover}
                        onMouseLeave={this.unsetHover}
                        onCopy={this.handleCopy}
                        data-a11y-sort-order='0'
                    >
                        <PostPreHeader
                            isFlagged={this.props.isFlagged}
                            isPinned={post.is_pinned}
                            channelId={post.channel_id}
                        />
                        <div
                            role='application'
                            className='post__content'
                        >
                            <div className='post__img'>
                                <PostProfilePicture
                                    compactDisplay={this.props.compactDisplay}
                                    isRHS={true}
                                    post={post}
                                    userId={post.user_id}
                                    channelId={post.channel_id}
                                    onProfilePopoverOpen={this.handleProfilePopoverOpen}
                                />
                            </div>
                            <div>
                                <div
                                    className='post__header'
                                    ref={this.postHeaderRef}
                                >
                                    <div className='col__name'>
                                        {userProfile}
                                        {botIndicator}
                                        {customStatus}
                                    </div>
                                    <div className='col'>
                                        {this.renderPostTime(isEphemeral)}
                                        {postInfoIcon}
                                    </div>
                                    {!isPostBeingEdited && dotMenuContainer}
                                </div>
                                <div className='post__body'>
                                    <div className={postClass}>
                                        <AutoHeightSwitcher
                                            showSlot={showSlot}
                                            shouldScrollIntoView={isPostBeingEdited}
                                            slot1={message}
                                            slot2={<EditPost isRHS={true} />}
                                            onTransitionEnd={() =>
                                                document.dispatchEvent(new Event(AppEvents.FOCUS_EDIT_TEXTBOX))
                                            }
                                        />
                                    </div>
                                    {fileAttachment}
                                    <ReactionList post={post} />
                                </div>
                            </div>
                        </div>
                    </PostAriaLabelDiv>
                </PostContextMenu>
            </PostContext.Provider>
        );
    }
}
